import { useState } from "react";
import { PrimaryButton, GhostButton } from "@/components/StandardButton";
import { Calendar, Clock, Loader2, Mail, Send, Check } from "lucide-react";
import { DisplayM, BodyText, Caption } from "@/components/ui/typography";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { addDays, addMonths, setDay, setDate } from "date-fns";

interface CampaignStepProps {
  pageId: string;
  pageTitle: string;
  companyName: string;
  onNext: () => void;
  onSkip: () => void;
}

type CampaignTemplate = 'one-time' | 'weekly' | 'monthly' | null;

const CAMPAIGN_TEMPLATES = [
  {
    id: 'one-time' as const,
    title: 'One-Time Send',
    description: 'Send once, right now',
    icon: Send,
    schedule: 'Sends immediately',
  },
  {
    id: 'weekly' as const,
    title: 'Weekly Pulse',
    description: 'Quick check-in every Friday',
    icon: Clock,
    schedule: 'Every Friday at 9 AM',
    recommended: true,
  },
  {
    id: 'monthly' as const,
    title: 'Monthly Check-in',
    description: 'In-depth feedback monthly',
    icon: Calendar,
    schedule: '1st of each month',
  },
];

export const CampaignStep = ({ 
  pageId, 
  pageTitle, 
  companyName, 
  onNext, 
  onSkip 
}: CampaignStepProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<CampaignTemplate>(null);
  const [recipients, setRecipients] = useState("");
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();

  const parseRecipients = (input: string): string[] => {
    return input
      .split(/[,\n;]/)
      .map(email => email.trim().toLowerCase())
      .filter(email => email && email.includes('@'));
  };

  const calculateNextSend = (template: CampaignTemplate): Date => {
    const now = new Date();
    
    if (template === 'one-time') {
      // Send in 5 minutes
      return addDays(now, 0);
    }
    
    if (template === 'weekly') {
      let nextFriday = setDay(now, 5);
      if (nextFriday <= now) {
        nextFriday = addDays(nextFriday, 7);
      }
      nextFriday.setHours(9, 0, 0, 0);
      return nextFriday;
    }
    
    if (template === 'monthly') {
      let firstOfMonth = setDate(now, 1);
      if (firstOfMonth <= now) {
        firstOfMonth = addMonths(firstOfMonth, 1);
      }
      firstOfMonth.setHours(9, 0, 0, 0);
      return firstOfMonth;
    }
    
    return addDays(now, 7);
  };

  const handleCreateCampaign = async () => {
    if (!selectedTemplate) {
      toast({
        title: "Select a schedule",
        description: "Choose when to send feedback requests",
        variant: "destructive",
      });
      return;
    }

    const emails = parseRecipients(recipients);
    if (emails.length === 0) {
      toast({
        title: "Add recipients",
        description: "Enter at least one email address",
        variant: "destructive",
      });
      return;
    }

    setCreating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const template = CAMPAIGN_TEMPLATES.find(t => t.id === selectedTemplate)!;
      const nextSendAt = calculateNextSend(selectedTemplate);

      const { error } = await supabase.from('feedback_campaigns').insert({
        user_id: user.id,
        page_id: pageId,
        name: selectedTemplate === 'one-time' 
          ? `${companyName} Feedback Request` 
          : `${companyName} ${template.title}`,
        description: selectedTemplate === 'one-time'
          ? 'One-time feedback collection'
          : `Automated ${selectedTemplate} feedback collection`,
        schedule_type: selectedTemplate === 'one-time' ? 'once' : selectedTemplate,
        recipient_emails: emails,
        recipients: emails.map(email => ({ email, name: '' })),
        subject_line: `Share your feedback with ${companyName}`,
        custom_message: `Hi there!\n\nWe'd love to hear your honest thoughts about ${companyName}.\n\nAll responses are 100% anonymous - we cannot see who submitted what.\n\nThank you for helping us improve!`,
        from_name: companyName,
        is_active: true,
        allow_anonymous: true,
        send_reminders: selectedTemplate !== 'one-time',
        reminder_days: 3,
        time_of_day: '09:00',
        day_of_week: selectedTemplate === 'weekly' ? 5 : null,
        day_of_month: selectedTemplate === 'monthly' ? 1 : null,
        next_send_at: nextSendAt.toISOString(),
        schedule_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      if (error) throw error;

      toast({
        title: selectedTemplate === 'one-time' ? "Sending soon!" : "Campaign created!",
        description: selectedTemplate === 'one-time' 
          ? "Your feedback request will be sent shortly."
          : `Your ${selectedTemplate} campaign is set up.`,
      });

      onNext();
    } catch (err: any) {
      toast({
        title: "Failed to create campaign",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const validEmails = parseRecipients(recipients);
  const canCreate = selectedTemplate && validEmails.length > 0;

  return (
    <div className="p-2 sm:p-4">
      {/* Header - compact */}
      <div className="text-center mb-4">
        <DisplayM className="mb-1">Schedule feedback emails</DisplayM>
        <BodyText className="text-muted-foreground text-sm">
          Automate feedback collection so you never forget.
        </BodyText>
      </div>

      {/* Template selection - compact 3 column grid */}
      <div className="flex flex-col sm:grid sm:grid-cols-3 gap-2 mb-4">
        {CAMPAIGN_TEMPLATES.map((template) => {
          const isSelected = selectedTemplate === template.id;
          return (
            <button
              key={template.id}
              onClick={() => setSelectedTemplate(template.id)}
              className={cn(
                "relative flex flex-col items-center p-2.5 rounded-2xl border text-center transition-all",
                isSelected
                  ? "border-primary bg-primary/5"
                  : "bg-card hover:bg-accent hover:border-border"
              )}
            >
              {template.recommended && (
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] font-medium bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                  Popular
                </span>
              )}
              <div className={cn(
                "h-8 w-8 rounded-full flex items-center justify-center mb-1.5",
                isSelected 
                  ? "bg-primary text-primary-foreground" 
                  : "bg-muted"
              )}>
                {isSelected ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <template.icon className="h-4 w-4" />
                )}
              </div>
              <p className="font-medium text-sm leading-tight">{template.title}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{template.schedule}</p>
            </button>
          );
        })}
      </div>

      {/* Recipients input - compact */}
      <div className="space-y-1.5 mb-4">
        <Label className="text-sm">Recipients</Label>
        <Textarea
          placeholder="Enter emails, separated by commas&#10;e.g. john@company.com, jane@company.com"
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
          rows={2}
          className="resize-none text-sm"
        />
        {recipients && (
          <Caption className={cn(
            validEmails.length > 0 ? "text-muted-foreground" : "text-destructive"
          )}>
            {validEmails.length > 0 
              ? `${validEmails.length} email${validEmails.length !== 1 ? 's' : ''} detected`
              : 'No valid emails detected'
            }
          </Caption>
        )}
      </div>

      {/* Email Preview - compact */}
      {selectedTemplate && (
        <div className="bg-muted/30 rounded-2xl p-2.5 border mb-4">
          <Caption className="text-muted-foreground mb-1">Email Preview</Caption>
          <p className="text-sm font-medium">
            Subject: Share your feedback with {companyName}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
            Hi there! We'd love to hear your honest thoughts...
          </p>
        </div>
      )}

      {/* Footer Actions */}
      <div className="flex gap-2">
        <GhostButton onClick={onSkip} className="flex-1 min-h-[44px] text-sm">
          Skip
        </GhostButton>
        <PrimaryButton 
          onClick={handleCreateCampaign} 
          className="flex-1 min-h-[44px] text-sm"
          disabled={creating || !canCreate}
        >
          {creating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Creating...
            </>
          ) : (
            selectedTemplate === 'one-time' ? "Send Now" : "Create Campaign"
          )}
        </PrimaryButton>
      </div>
    </div>
  );
};
